import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SoundPlayer from "@/components/SoundPlayer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronDown, ChevronUp, AlertTriangle, Headphones, Clock, Repeat, Shield, MapPin } from "lucide-react";
import { setCanonical } from "@/utils/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CityConditionPage() {
  const { conditionSlug, citySlug } = useParams();
  const [data, setData] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    axios.get(`${API}/city-therapy/${citySlug}/${conditionSlug}`).then(r => {
      setData(r.data);
      setLoading(false);
      const city = r.data.city;
      const condition = r.data.condition;
      document.title = `Sound Therapy for ${condition.name} in ${city.city}, ${city.state_abbr} | ToneHealing`;
      setCanonical(`/therapy/${conditionSlug}/in/${citySlug}`);
    }).catch(() => setLoading(false));
  }, [conditionSlug, citySlug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-16"><div className="animate-pulse text-muted-foreground">Loading therapy...</div></div>;
  if (!data) return <div className="min-h-screen flex flex-col items-center justify-center pt-16 gap-4"><p className="text-muted-foreground">Page not found</p><Link to="/"><Button variant="outline">Back to Home</Button></Link></div>;

  const { city, condition, related_conditions, nearby_cities, recent_posts, solfeggio_info, brainwave_info } = data;

  const brainwaveColorClass = {
    Delta: "text-violet-400", Theta: "text-primary", Alpha: "text-accent", Beta: "text-orange-400"
  }[condition.brainwave_type] || "text-primary";

  const cityStressFactors = city.risk_factors || getDefaultStressFactors(city);

  const faqs = [
    { q: `Why is sound therapy helpful for people in ${city.city}?`, a: `${city.city}, ${city.state} has unique stressors that affect residents' well-being. ${cityStressFactors} Sound therapy with ${condition.brainwave_type} waves at ${condition.frequency}Hz specifically targets ${condition.name.toLowerCase()} symptoms, providing a free, accessible wellness tool for ${city.city} residents — no appointment needed, available 24/7.` },
    { q: `How do binaural beats help with ${condition.name.toLowerCase()} in ${city.city}?`, a: `Binaural beats at ${condition.frequency}Hz create ${condition.brainwave_type} wave entrainment in your brain. ${condition.description} When you listen with headphones, your brain synchronizes to this frequency, naturally shifting into a ${condition.brainwave_type.toLowerCase()} state that directly addresses ${condition.name.toLowerCase()}.` },
    { q: `How long should ${city.city} residents listen for ${condition.name.toLowerCase()} therapy?`, a: `We recommend starting with 10-15 minute sessions. ${condition.brainwave_type === "Delta" ? "Delta waves promote deep states, so 20-30 minute sessions are especially beneficial before sleep." : condition.brainwave_type === "Beta" ? "Beta wave sessions can be shorter (10-15 minutes) as they are stimulating." : "Gradually increase to 20-30 minutes as you become accustomed to the frequencies."} Consistency matters more than duration — daily sessions produce the best results.` },
    { q: `Is sound therapy free in ${city.city}?`, a: `Yes! ToneHealing.com is completely free to use. No account, no downloads, no subscriptions. Just put on headphones and press play. Our sound therapy is available 24/7 for all ${city.city} residents and visitors.` },
  ];

  return (
    <div data-testid="city-condition-page">
      {/* Hero */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 overflow-hidden" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero-bg-2.png)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <Link to={`/therapy/${condition.slug}`} className="inline-flex items-center gap-2 text-base font-semibold text-primary hover:text-primary/80 mb-6 transition-colors bg-white/60 px-4 py-2 rounded-full" data-testid="back-link">
                <ArrowLeft className="w-5 h-5" /> Back to {condition.name} Therapy
              </Link>
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="border-primary/30 bg-white/60">
                  <MapPin className="w-3 h-3 mr-1" /> {city.city}, {city.state_abbr}
                </Badge>
                <Badge variant="outline" className="border-border/30 bg-white/60">
                  <span className={brainwaveColorClass}>{condition.brainwave_type} Waves</span>
                </Badge>
                <Badge variant="outline" className="border-border/30 bg-white/60 font-mono-freq">{condition.frequency}Hz</Badge>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4" data-testid="city-condition-title">
                Sound Therapy for <span className={brainwaveColorClass}>{condition.name}</span> in {city.city}, {city.state_abbr}
              </h1>
              <p className="text-base text-muted-foreground max-w-2xl leading-relaxed">{condition.description}</p>

              {/* GEO: Definition-lead sentence for AI engine citation */}
              <p className="speakable-intro text-xs text-muted-foreground/60 mt-3 max-w-2xl">
                Sound therapy for {condition.name} in {city.city}, {city.state_abbr} is a free wellness practice using {condition.brainwave_type} wave binaural beats at {condition.frequency}Hz — available 24/7, no account or appointment required.
              </p>
            </div>

            {/* Meditation bubble — local image */}
            <div className="hidden lg:flex items-center justify-center relative">
              <div className="absolute w-48 h-48 rounded-full border-2 border-primary/20" style={{ animation: 'ring-pulse-1 3s ease-in-out infinite' }} />
              <div className="absolute w-64 h-64 rounded-full border border-primary/10" style={{ animation: 'ring-pulse-2 4s ease-in-out infinite 0.5s' }} />
              <img
                src="/hero-meditation.png"
                alt={`Sound therapy for ${condition.name} in ${city.city}`}
                className="relative z-10 w-44 h-44 object-cover rounded-full drop-shadow-2xl animate-wave-float"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Content */}
            <div className="lg:col-span-5 space-y-10 order-2 lg:order-1">
              <div>
                <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Why {city.city} Residents Need {condition.name} Relief
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Living in {city.city}, {city.state} comes with unique challenges that can contribute to {condition.name.toLowerCase()}. {cityStressFactors}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Sound therapy offers {city.city} residents a free, accessible way to address {condition.name.toLowerCase()} from the comfort of home. Our {condition.brainwave_type} wave therapy at {condition.frequency}Hz uses scientifically-backed binaural beats to naturally guide your brain into a relaxed state — no appointments, no prescriptions, no cost.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  How {condition.brainwave_type} Wave Therapy Works
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  When you listen with headphones, your brain receives {condition.base_frequency}Hz in one ear and {condition.base_frequency + condition.frequency}Hz in the other. Your brain perceives the {condition.frequency}Hz difference as a gentle pulse, synchronizing your brainwaves to the {condition.brainwave_type} range ({brainwave_info?.range}).
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {condition.brainwave_type} waves are associated with {brainwave_info?.state?.toLowerCase()}. Combined with the {condition.solfeggio}Hz Solfeggio frequency{solfeggio_info ? ` (${solfeggio_info.name} — ${solfeggio_info.description.toLowerCase()})` : ''}, this creates a comprehensive wellness experience tailored for {condition.name.toLowerCase()}.
                </p>
              </div>

              {/* Tips */}
              <div>
                <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Tips for Best Results</h2>
                <div className="space-y-2">
                  {[
                    { icon: Headphones, text: "Use headphones — essential for binaural beats" },
                    { icon: Clock, text: `Start with ${condition.brainwave_type === "Beta" ? "10" : "15"}-minute sessions` },
                    { icon: Repeat, text: "Daily sessions for best results" },
                    { icon: Shield, text: "Do not use while driving" },
                  ].map((tip, i) => (
                    <div key={i} className="flex items-center gap-2 glass rounded-lg p-3">
                      <tip.icon className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-xs text-muted-foreground">{tip.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div data-testid="city-condition-faq">
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Frequently Asked Questions</h2>
                <div className="space-y-3">
                  {faqs.map((faq, i) => (
                    <div key={i} className="glass rounded-xl overflow-hidden">
                      <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                        <span className="text-sm font-medium pr-4">{faq.q}</span>
                        {expandedFaq === i ? <ChevronUp className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />}
                      </button>
                      {expandedFaq === i && <div className="px-5 pb-5"><p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Player */}
            <div className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start order-1 lg:order-2">
              <SoundPlayer condition={condition} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-3 space-y-6 order-3">
              <div className="ad-sidebar p-3 text-center">
                <div className="bg-secondary/50 rounded-lg p-6 min-h-[200px] flex flex-col items-center justify-center">
                  <p className="text-xs text-muted-foreground/50 font-medium">Your Ad Here</p>
                  <p className="text-[10px] text-muted-foreground/30 mt-1">support@lifeownit.com</p>
                </div>
              </div>
              {related_conditions?.length > 0 && (
                <div className="glass rounded-xl p-4">
                  <h4 className="text-sm font-semibold mb-3">More Therapies in {city.city}</h4>
                  <div className="space-y-2">
                    {related_conditions.map(r => (
                      <Link key={r.slug} to={`/therapy/${r.slug}/in/${city.slug}`} className="block text-sm text-primary hover:underline">
                        {r.name} in {city.city}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Nearby Cities — Internal Linking */}
      {nearby_cities && nearby_cities.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Sound Therapy for {condition.name} in Nearby Cities</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {nearby_cities.map(nc => (
                <Link key={nc.slug} to={`/therapy/${condition.slug}/in/${nc.slug}`} className="glass rounded-xl p-4 hover:shadow-lg transition-all group text-center">
                  <p className="text-sm font-medium group-hover:text-primary transition-colors">{nc.city}, {nc.state_abbr}</p>
                  <p className="text-xs text-muted-foreground mt-1">Pop: {nc.population?.toLocaleString()}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Articles — Internal Linking */}
      {recent_posts && recent_posts.length > 0 && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Related Articles from ToneHealing</h3>
            <div className="flex flex-wrap gap-3">
              {recent_posts.map(post => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="text-sm text-primary hover:underline bg-white/60 px-3 py-2 rounded-lg">
                  {post.title}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Safety */}
      <section className="py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              <h3 className="text-sm font-bold text-destructive">Important Safety Warning</h3>
            </div>
            <p className="text-xs text-destructive/80 leading-relaxed">
              DO NOT use sound therapy while driving, operating machinery, or during any activity requiring alertness. Not recommended for epilepsy or seizure disorders.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom AdSense */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-4">
          <div className="rounded-2xl border border-dashed border-primary/20 bg-white/60 p-8 text-center min-h-[100px] flex flex-col items-center justify-center">
            <p className="text-sm text-muted-foreground/40">Google AdSense</p>
            <p className="text-xs text-muted-foreground/25 mt-1">Ad space</p>
          </div>
        </div>
      </section>

      {/* Schema — Google AI Mode + GEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `Sound Therapy for ${condition.name} in ${city.city}, ${city.state_abbr}`,
        "description": `Free ${condition.brainwave_type} wave sound therapy for ${condition.name.toLowerCase()} in ${city.city}, ${city.state}. Binaural beats at ${condition.frequency}Hz and Solfeggio frequencies. No account required.`,
        "provider": { "@type": "Organization", "name": "ToneHealing", "url": "https://tonehealing.com" },
        "areaServed": { "@type": "City", "name": city.city, "containedInPlace": { "@type": "State", "name": city.state } },
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock" }
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://tonehealing.com" },
          { "@type": "ListItem", "position": 2, "name": `${condition.name} Therapy`, "item": `https://tonehealing.com/therapy/${condition.slug}` },
          { "@type": "ListItem", "position": 3, "name": `${city.city}, ${city.state_abbr}`, "item": `https://tonehealing.com/therapy/${condition.slug}/in/${city.slug}` },
        ]
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": `Sound Therapy for ${condition.name} in ${city.city}`,
        "description": `Free ${condition.brainwave_type} wave sound therapy for ${condition.name.toLowerCase()} available in ${city.city}, ${city.state}. Science-backed binaural beats, no account required.`,
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro"] },
        "url": `https://tonehealing.com/therapy/${condition.slug}/in/${city.slug}`
      })}} />
    </div>
  );
}

function getDefaultStressFactors(city) {
  const stateFactors = {
    "Texas": "The extreme Texas heat, long commute times, and high cost of living in major metro areas contribute to elevated stress and anxiety levels.",
    "California": "High cost of living, wildfire anxiety, traffic congestion, and earthquake risk create unique stressors for California residents.",
    "New York": "Fast-paced lifestyle, high cost of living, overcrowding, noise pollution, and long work hours make stress management essential.",
    "Florida": "Hurricane anxiety, extreme humidity, tourism-driven economy fluctuations, and rapid population growth affect mental well-being.",
    "Illinois": "Harsh winters, seasonal depression, urban noise in Chicago, and economic uncertainty create multiple stress triggers.",
    "Colorado": "Altitude adjustment, dry climate effects, rapid population growth, and seasonal weather changes impact wellness.",
    "Arizona": "Extreme desert heat, limited outdoor activity months, rapid urbanization, and water scarcity concerns affect daily life.",
    "Georgia": "Humid subtropical climate, urban sprawl, traffic congestion in Atlanta, and seasonal allergens contribute to health challenges.",
    "Ohio": "Industrial economic transitions, harsh winters, and rural health access gaps create unique wellness needs.",
    "Pennsylvania": "Historic industrial decline in some areas, seasonal affective disorder from cold winters, and urban density stress.",
  };
  return stateFactors[city.state] || `The unique environment and lifestyle in ${city.city}, ${city.state} creates specific wellness challenges that sound therapy can help address. Local stressors including weather, commute times, cost of living, and urban density all contribute to conditions like anxiety, insomnia, and chronic stress.`;
}
